import React, { useCallback, useState } from 'react';
import { GraphLinksResponse } from '../../../../services/performanceMetricsService';
import { ColorBy, getCategoryColor, getCommunityColor } from './linkGraphUtils';

type LinkGraphLegendProps = {
  data: GraphLinksResponse;
  colorBy: ColorBy;
  showOrphans: boolean;
  selectedKey: string | number | null;
  setSelectedKey: React.Dispatch<React.SetStateAction<string | number | null>>;
};

export const LinkGraphLegend: React.FC<LinkGraphLegendProps> = ({
  data,
  colorBy,
  showOrphans,
  selectedKey,
  setSelectedKey,
}) => {
  const [expandedCommunities, setExpandedCommunities] = useState<Set<number>>(new Set());

  const toggleExpanded = useCallback((communityId: number) => {
    setExpandedCommunities((prev) => {
      const next = new Set(prev);
      if (next.has(communityId)) {
        next.delete(communityId);
      } else {
        next.add(communityId);
      }
      return next;
    });
    setSelectedKey((prev) => (prev === communityId ? null : communityId));
  }, [setSelectedKey]);

  if (colorBy === 'none') return null;

  return (
    <div className="absolute bottom-3 left-3 bg-white/90 rounded border border-gray-200 px-3 py-2 text-xs max-h-72 overflow-y-auto" style={{ minWidth: 180 }}>
      {colorBy === 'community' || colorBy === 'sub_community' ? (
        (data.communities || []).map((c) => {
          const isExpanded = expandedCommunities.has(c.id);
          const isSelected = selectedKey === c.id;
          const hasSubs = c.sub_communities.length > 1;
          return (
            <div key={c.id}>
              <div
                className={`flex items-center gap-2 py-0.5 cursor-pointer hover:bg-gray-100 -mx-1 px-1 rounded ${isSelected ? 'bg-cyan-50' : ''}`}
                onClick={() => toggleExpanded(c.id)}
              >
                {hasSubs ? (
                  <span className="w-3 text-gray-400 text-[10px] shrink-0">{isExpanded ? '▾' : '▸'}</span>
                ) : (
                  <span className="w-3 shrink-0" />
                )}
                <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: getCommunityColor(c.id) }} />
                <span className={`text-gray-600 ${isSelected ? 'font-medium' : ''}`}>
                  Community {c.id} ({c.size})
                </span>
              </div>
              {isExpanded && hasSubs && (
                <div className="ml-5 space-y-0.5 mb-1">
                  {c.sub_communities.map((sc) => (
                    <div key={sc.id} className="flex items-center gap-2 py-0.5">
                      <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: getCommunityColor(sc.id) }} />
                      <span className="text-gray-500">Sub {sc.id} ({sc.size})</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })
      ) : colorBy === 'category' ? (
        Array.from(new Set(data.nodes.map((n) => n.category).filter(Boolean) as string[])).sort().map((cat) => {
          const isSelected = selectedKey === cat;
          return (
            <div
              key={cat}
              className={`flex items-center gap-2 py-0.5 cursor-pointer hover:bg-gray-100 -mx-1 px-1 rounded ${isSelected ? 'bg-cyan-50' : ''}`}
              onClick={() => setSelectedKey((prev) => (prev === cat ? null : cat))}
            >
              <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: getCategoryColor(cat) }} />
              <span className={`text-gray-600 ${isSelected ? 'font-medium' : ''}`}>{cat}</span>
            </div>
          );
        })
      ) : null}
      {showOrphans && (
        <div className="flex items-center gap-2 mt-1 pt-1 border-t border-gray-200">
          <div className="w-2.5 h-2.5 rounded-full shrink-0 bg-gray-200 border border-gray-300" />
          <span className="text-gray-400">Orphan (no links)</span>
        </div>
      )}
    </div>
  );
};
